import React, { useEffect, useRef, useState } from 'react';
import { CodeBenchv2 } from '@codametrix/shared-views';
import { ServiceDesk } from './service-desk';
import { useSelector, useDispatch } from 'react-redux';
import {
  PatientEncounterGradeBenchColumnsv2,
  uiActions,
  codeBenchActions,
  commonEnums
} from '@codametrix/ui-common';
import history from '../../history';
import { setBackToWorklistPath } from '../../actions/ui';

const BenchMode = commonEnums.BenchMode;

type DualComponentProps = {
  detailProps: any;
  detailMethods: any;
  listProps: any;
  listMethods: any;
  match: any;
};

const enabled = { value: true, reasons: [] };

const onCapability: CMxCommonApp.Capability = {
  on: enabled,
  actionable: enabled
};

const ServiceDeskAndGradeBench = (props: DualComponentProps) => {
  const { params = {} } = props.match ?? {};

  const { UIView } = useSelector((state: CMx.ApplicationState) => {
    return state.ampUI;
  });
  const activeContext = useSelector(
    (state: CMx.ApplicationState) => state.ui.context.activeContext
  );
  const tenantId = activeContext?.tenantId;
  const serviceLineId = useSelector(
    (state: CMx.ApplicationState) => state.ui.serviceLine?.id
  );
  const backToWorklistPath = useSelector(
    (state: CMx.ApplicationState) => state.ui.backToWorklistPath
  );
  const dispatch = useDispatch();
  const childRef = useRef<any>(null);

  const codeBenchPanelData: Accelerate.CodeBenchPanelData = {
    ...props.detailProps,
    capabilities: {
      ...props.detailProps.capabilities,
      capabilities: {
        ...props.detailProps.capabilities.capabilities,
        attachDocs: { on: false },
        improvements: { on: false },
        coderNotes: { on: false },
        versionProcess: { on: false },
        statusDescription: { on: false },
        actionMenu: { on: false },
        submitCase: onCapability
      }
    },
    propertyConfig: PatientEncounterGradeBenchColumnsv2,
    gradeableInvoices: props.detailProps.gradeableInvoices
  };

  const detailProps = {
    UIView,
    ...codeBenchPanelData,
    ...props.detailMethods
  };

  const listProps = {
    ...props.listProps,
    ...props.listMethods
  };

  const [isDetailOpen, setIsDetailOpen] = useState<boolean>(true);

  const handleSelection = (item: CMxAPI.CaseRunV2) => {
    setIsDetailOpen(true);
  };

  useEffect(() => {
    if (params.id === undefined && childRef?.current?.refreshContent) {
      childRef.current.refreshContent();
    }
    setIsDetailOpen(params.id !== undefined);
  }, [params.id]);

  useEffect(() => {
    dispatch(uiActions.setAmplifyView());
    dispatch(uiActions.setStandaloneMode());
    dispatch(codeBenchActions.setBenchMode(BenchMode.GRADE));
    return () => {
      dispatch(uiActions.setAutomateView());
    };
  }, [dispatch]);

  useEffect(() => {
    if (params.id === undefined) {
      return;
    }
    const { pathname, search, hash } = history.location;
    const searchParams = new URLSearchParams(search);
    let mutated = false;
    if (tenantId && !searchParams.has('tenantId')) {
      searchParams.set('tenantId', tenantId);
      mutated = true;
    }
    if (serviceLineId !== undefined && !searchParams.has('serviceLineId')) {
      searchParams.set('serviceLineId', `${serviceLineId}`);
      mutated = true;
    }
    if (mutated) {
      const nextSearch = searchParams.toString();
      history.replace({
        pathname,
        search: nextSearch ? `?${nextSearch}` : '',
        hash: hash ?? '',
        state: history.location.state
      });
    }
  }, [params.id, tenantId, serviceLineId]);

  useEffect(() => {
    if (params.id === undefined) {
      return;
    }
    const searchParams = new URLSearchParams(history.location.search);
    const projectId = searchParams.get('projectId');
    if (!projectId) {
      return;
    }
    const params_ = new URLSearchParams();
    params_.set('projectId', projectId);
    const status = searchParams.get('status');
    if (status) {
      params_.set('status', status);
    }
    // The URL is authoritative: reconcile backToWorklistPath to the project in
    // the URL. This corrects a stale or bare path (e.g. a default
    // 'quality-assessment/') left over from an earlier mount so "Back to
    // Worklist" returns to the project the user actually came from.
    const desiredPath = `quality-assessment/?${params_.toString()}`;
    if (backToWorklistPath !== desiredPath) {
      dispatch(setBackToWorklistPath({ path: desiredPath }));
    }
  }, [params.id, backToWorklistPath, dispatch, history.location.search]);

  if (!activeContext) {
    return null;
  }

  const urlTenantId = new URLSearchParams(history.location.search).get(
    'tenantId'
  );
  if (params.id !== undefined && urlTenantId && urlTenantId !== tenantId) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          gap: '8px'
        }}>
        <h2>403 — Access Denied</h2>
        <p>
          You do not have access to this case in the current organization
          context.
        </p>
      </div>
    );
  }

  return (
    <>
      <div style={{ display: isDetailOpen ? 'none' : 'block' }}>
        {/* idk why are we rendering service desk */}
        <ServiceDesk
          {...listProps}
          childRef={childRef}
          onSelection={handleSelection}
        />
      </div>
      {isDetailOpen ? (
        <CodeBenchv2
          {...detailProps}
          match={props.match}
          benchMode={BenchMode.GRADE}
        />
      ) : null}
    </>
  );
};

export { ServiceDeskAndGradeBench };
